import { AdPlacement } from '@openx/types/adUnit';
import { DistributionChannel } from '@openx/types/distributionChannel';
import { InventoryFormatTypeOption } from '@openx/types/targeting/formatType';
import { TargetingFields, type TargetingFieldsValues } from '@openx/types/targeting/targeting';
import { RequiredTargetingFormatTypeOption } from '@openx/types/targeting/targetingPresets';
import { ComparisonType, Criteria, Intersect } from '@openx/types/targeting/targetingValuesTypes';
import type { DealPromptState } from '../../../constants';
import { dealPromptContextTypes } from '../../types';

type FormatAndDevicesFilters = {
  format?: string[];
  ds_device?: string[];
};

type DealTargeting = Partial<Record<TargetingFieldsValues, any>>;

const DEVICE_GROUP_IDS = new Set<string>(['mobile/tablet', 'tv_devices']);

const DEVICE_BUCKETS = {
  desktop: new Set<string>(['desktop']),
  mobile: new Set<string>(['phone', 'tablet']),
  tv: new Set<string>(['connected tv', 'set-top-box']),
} as const;

const DEVICE_VALUE_MAP: Record<string, string> = {
  desktop: 'desktop',
  phone: 'phone',
  tablet: 'tablet',
  'connected tv': 'tv',
  'set-top-box': 'set-top-box',
};

type DeviceTypeTargetingValue = {
  op: typeof Intersect.INTERSECTS;
  desktop_devices?: string;
  mobile_devices?: string;
  tv_devices?: string;
};

const uniq = (arr: string[]) => Array.from(new Set(arr));

const toCsvOrUndefined = (vals: string[]) => {
  const cleaned = uniq(vals).filter(Boolean);
  return cleaned.length ? cleaned.join(',') : undefined;
};

const buildDeviceTypeTargetingValue = (dsDevice?: string[]): DeviceTypeTargetingValue | undefined => {
  const selected = (dsDevice ?? []).map(v => v.trim()).filter(v => v && !DEVICE_GROUP_IDS.has(v));

  if (!selected.length) return undefined;

  const desktopVals: string[] = [];
  const mobileVals: string[] = [];
  const tvVals: string[] = [];

  selected.forEach(id => {
    const mapped = DEVICE_VALUE_MAP[id];
    if (!mapped) return;

    if (DEVICE_BUCKETS.desktop.has(id)) desktopVals.push(mapped);
    if (DEVICE_BUCKETS.mobile.has(id)) mobileVals.push(mapped);
    if (DEVICE_BUCKETS.tv.has(id)) tvVals.push(mapped);
  });

  const desktopCsv = toCsvOrUndefined(desktopVals);
  const mobileCsv = toCsvOrUndefined(mobileVals);
  const tvCsv = toCsvOrUndefined(tvVals);

  if (!desktopCsv && !mobileCsv && !tvCsv) return undefined;

  return {
    op: Intersect.INTERSECTS,
    ...(desktopCsv ? { desktop_devices: desktopCsv } : {}),
    ...(mobileCsv ? { mobile_devices: mobileCsv } : {}),
    ...(tvCsv ? { tv_devices: tvCsv } : {}),
  };
};

const mapFormatToAdPlacement = (format?: string[]): AdPlacement | undefined => {
  const first = format?.[0];
  if (!first) return undefined;

  switch (first.toLowerCase()) {
    case 'banner':
      return AdPlacement.Banner;
    case 'video':
      return AdPlacement.Video;
    case 'native':
      return AdPlacement.Native;
    case 'ctv':
      return AdPlacement.CTV;
    default:
      return undefined;
  }
};

export const setInventoryFormatTargetingFromMergedFilters = (
  prevTargeting: DealTargeting,
  filters?: FormatAndDevicesFilters,
  contextType?: DealPromptState['contextType']
): DealTargeting => {
  if (!filters) {
    return prevTargeting;
  }
  const hasFormat = Array.isArray(filters.format) && filters.format.length > 0 && !!filters.format[0];
  const hasDevices = Array.isArray(filters.ds_device) && filters.ds_device.length > 0;

  if (!hasFormat && !hasDevices) {
    const next = { ...prevTargeting };
    delete next[TargetingFields.INVENTORY_FORMAT];
    return next;
  }

  const next: DealTargeting = { ...prevTargeting };
  const inventoryFormat = {
    ...(next[TargetingFields.INVENTORY_FORMAT] ?? {}),
  } as Record<string, unknown>;

  if (contextType) {
    let distributionChannelValue;

    if (contextType === dealPromptContextTypes.DOMAINS) {
      distributionChannelValue = DistributionChannel.Web;
    } else if (contextType === dealPromptContextTypes.APPS) {
      distributionChannelValue = DistributionChannel.App;
    }

    const distributionChannelData = {
      op: Intersect.INTERSECTS,
      val: distributionChannelValue,
    };

    inventoryFormat[InventoryFormatTypeOption.DISTRIBUTION_CHANNEL] = distributionChannelData;
  }

  if (hasFormat) {
    const adPlacement = mapFormatToAdPlacement(filters.format);

    if (adPlacement) {
      const adPlacementData = {
        op: ComparisonType.EQUALS,
        val: adPlacement,
      };

      inventoryFormat[InventoryFormatTypeOption.AD_PLACEMENT] = adPlacementData;
    } else {
      delete inventoryFormat[InventoryFormatTypeOption.AD_PLACEMENT];
    }
  } else {
    delete inventoryFormat[InventoryFormatTypeOption.AD_PLACEMENT];
  }

  if (hasFormat && hasDevices) {
    const deviceTypeValue = buildDeviceTypeTargetingValue(filters.ds_device);

    if (deviceTypeValue) {
      inventoryFormat[RequiredTargetingFormatTypeOption.DEVICE_TYPE] = deviceTypeValue;
    } else {
      delete inventoryFormat[RequiredTargetingFormatTypeOption.DEVICE_TYPE];
    }
  } else {
    delete inventoryFormat[RequiredTargetingFormatTypeOption.DEVICE_TYPE];
  }

  next[TargetingFields.INVENTORY_FORMAT] = { ...inventoryFormat, op: Criteria.ALL };
  return next;
};
